// =====================================================================
// secouristes.js — règles pures de l'écran « Secouristes & formations » (23-10-2026)
// =====================================================================
// Miroir des vérifications du serveur (backend/src/services/safety-secouristes.service.js,
// datesFiche + derogationFiche) : l'écran dit le problème AVANT l'envoi, avec les mêmes mots ;
// le serveur reste juge.
// =====================================================================

/** Secourisme : les mots de l'art. I.5-9 (recyclage, rattrapage, compétence). */
export const STATUTS = {
  VALID:       { label: 'valable',               bg: '#dcfce7', fg: '#166534' },
  A_PLANIFIER: { label: 'à former',              bg: '#fef3c7', fg: '#92400e' },
  RATTRAPAGE:  { label: 'recyclage dépassé',     bg: '#ffedd5', fg: '#9a3412' },
  EXPIRE:      { label: 'plus réputé compétent', bg: '#fee2e2', fg: '#991b1b' },
};

/** Autres formations : pas de rattrapage, la fin est celle du certificat. */
export const STATUTS_AUTRES = {
  VALID:       { label: 'valable',  bg: '#dcfce7', fg: '#166534' },
  A_PLANIFIER: { label: 'à faire',  bg: '#fef3c7', fg: '#92400e' },
  RATTRAPAGE:  { label: 'expirée',  bg: '#fee2e2', fg: '#991b1b' },
  EXPIRE:      { label: 'expirée',  bg: '#fee2e2', fg: '#991b1b' },
};

/** Les autres formations proposées à l'ajout (même liste que le serveur, testé). */
export const TYPES_AUTRES = [
  ['CARISTE', 'Cariste (chariot élévateur)'],
  ['SECURITE', 'Sécurité'],
  ['AUTRE', 'Autre formation'],
];

export const pastille = (s) => (s.type && s.type !== 'SECOURISME' ? STATUTS_AUTRES : STATUTS)[s.statut] || STATUTS.A_PLANIFIER;

/** AAAA-MM-JJ → JJ/MM/AAAA (vide si pas de date). */
export const enClair = (ymd) => (ymd ? ymd.split('-').reverse().join('/') : '');

/** La phrase qui dit où en est la personne — ce que Vincent lit en premier. */
export function phraseStatut(s, aujourdhui) {
  const prevue = s.prevueLe && s.prevueLe >= aujourdhui ? ` Prochaine séance prévue le ${enClair(s.prevueLe)}.` : '';
  const pasFaite = () => {
    if (!s.prevueLe) return 'Formation pas encore faite, aucune séance prévue.';
    if (s.prevueLe < aujourdhui) {
      return `Séance prévue le ${enClair(s.prevueLe)}, date passée : notez la formation si elle a eu lieu, sinon une nouvelle date.`;
    }
    return `Formation prévue le ${enClair(s.prevueLe)}.`;
  };
  if (s.type && s.type !== 'SECOURISME') {
    if (s.statut === 'A_PLANIFIER') return pasFaite();
    if (s.statut === 'VALID') {
      return (s.valableJusquAu ? `Valable jusqu’au ${enClair(s.valableJusquAu)}.` : 'Valable, sans date de fin sur le certificat.') + prevue;
    }
    return `Expirée depuis le ${enClair(s.valableJusquAu)} : à renouveler.${prevue}`;
  }
  const rythme = s.derogation2ans ? 'recyclage de 4 h tous les 2 ans (dérogation)' : 'recyclage de 4 h';
  switch (s.statut) {
    case 'VALID':
      return `Valable jusqu’au ${enClair(s.valableJusquAu)} : ${rythme} à faire avant cette date.${prevue}`;
    case 'RATTRAPAGE':
      return `Recyclage dépassé depuis le ${enClair(s.valableJusquAu)} : à rattraper avant le ${enClair(s.rattrapageJusquAu)}, sinon plus réputé compétent.${prevue}`;
    case 'EXPIRE':
      return `Plus réputé compétent depuis le ${enClair(s.rattrapageJusquAu)} : il faut refaire la formation.${prevue}`;
    default:
      return pasFaite();
  }
}

/**
 * Le problème d'une saisie, en clair (null = on peut enregistrer). Mêmes règles, même ordre et
 * mêmes mots que le serveur : d'abord les dates, puis la dérogation (secourisme seulement).
 */
export function erreurFiche({ faiteLe, prevueLe, valableJusquAu, derogation2ans, analyseRisque, type = 'SECOURISME' }, aujourdhui, prevueAvant = null) {
  const fin = type === 'SECOURISME' ? null : (valableJusquAu || null);
  if (faiteLe && faiteLe > aujourdhui) {
    return 'Une formation faite ne peut pas être dans le futur : pour une séance à venir, remplissez « prévue le ».';
  }
  if (faiteLe && faiteLe < '2000-01-01') return 'Date de formation trop ancienne : vérifiez l’année.';
  if (prevueLe && prevueLe < aujourdhui && prevueLe !== prevueAvant) {
    return 'Une séance prévue ne peut pas être dans le passé : effacez-la, ou notez la formation comme faite.';
  }
  if (fin && !faiteLe) return 'Une fin de validité suppose une formation faite : indiquez d’abord sa date.';
  if (fin && fin < faiteLe) return 'La fin de validité ne peut pas précéder la formation.';
  if (type === 'SECOURISME' && derogation2ans && (!analyseRisque || analyseRisque.trim().length < 3)) {
    return 'La dérogation (recyclage tous les 2 ans) exige l’analyse de risque : indiquez où elle est tenue à disposition du contrôle.';
  }
  return null;
}
